import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Problem, ReasonCode } from '../../../api';
import { reasonMessage } from '../../../i18n';

import { CodeStep } from './CodeStep';
import { PasswordStep } from './PasswordStep';
import { PhoneStep } from './PhoneStep';
import type { LoginView } from './login-view';

/** Props of {@link TelegramLoginScreen}. */
export interface TelegramLoginScreenProps {
  readonly view: LoginView;
  readonly pending: boolean;
  /** Epoch ms until which "Get a new code" stays disabled (Telegram's delay), or `null`. */
  readonly newCodeBlockedUntil: number | null;
  /** True when identification was lost: the banner shows above the phone step (R18). */
  readonly lost: boolean;
  readonly onSharePhone: () => void;
  readonly onTypePhone: () => void;
  readonly onSubmitPhone: (input: string) => void;
  readonly onSubmitCode: (code: string) => void;
  readonly onSubmitPassword: (password: string) => void;
  readonly onNewCode: () => void;
}

function ReasonAlert({
  code,
  params,
  severity,
}: {
  code: ReasonCode;
  params?: Problem['params'];
  severity: 'error' | 'warning';
}): ReactElement {
  const { t } = useTranslation();
  // I12: the text of the code, never a problem title or a body. Alert is role="alert" by default.
  const message = reasonMessage(code, params);
  return <Alert severity={severity}>{t(message.key, message.values)}</Alert>;
}

/**
 * Screen E2 · Telegram identification (specs/S4-telegram-login/spec.md; US-03): the step
 * indicator, the lost banner, the failure of the last action and the current step. Presentational:
 * it fetches nothing and holds no secret; the steps own their inputs. It owns its `main` landmark
 * because it renders outside `AppLayout` (R22).
 */
export function TelegramLoginScreen({
  view,
  pending,
  newCodeBlockedUntil,
  lost,
  onSharePhone,
  onTypePhone,
  onSubmitPhone,
  onSubmitCode,
  onSubmitPassword,
  onNewCode,
}: TelegramLoginScreenProps): ReactElement {
  const { t } = useTranslation();
  const notice = view.notice;
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        px: 2.5,
        pt: 3,
        pb: 3.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
        {t('onboarding.telegram.step')}
      </Typography>
      {lost && view.step === 'phone' ? (
        <ReasonAlert code="TELEGRAM_SESSION_LOST" severity="warning" />
      ) : null}
      {notice === null ? null : (
        <ReasonAlert code={notice.reasonCode} params={notice.params} severity="error" />
      )}
      {view.step === 'phone' ? (
        <PhoneStep
          mode={view.mode}
          pending={pending}
          onShare={onSharePhone}
          onType={onTypePhone}
          onSubmit={onSubmitPhone}
        />
      ) : view.step === 'code' ? (
        <CodeStep
          phoneMasked={view.phoneMasked}
          codeLength={view.codeLength}
          notice={notice}
          pending={pending}
          newCodeDisabled={newCodeBlockedUntil !== null}
          onSubmit={onSubmitCode}
          onNewCode={onNewCode}
        />
      ) : (
        <PasswordStep
          notice={notice}
          pending={pending}
          showNewCode={notice?.reasonCode === 'TELEGRAM_LOGIN_NOT_STARTED'}
          onSubmit={onSubmitPassword}
          onNewCode={onNewCode}
        />
      )}
      {pending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} aria-label={t('onboarding.telegram.sending')} />
        </Box>
      ) : null}
    </Box>
  );
}
